import { Journey, journeyToJourneyDetails } from '@audibene/ta.utils-opportunity'
import { Button, Grid, styled, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AppointmentType } from '../../../constants'
import { useBlockedAppointmentType, useEventTracking, useShouldRestrictToHAE, useSearchParameters } from '../../../hooks'
// Imported by path rather than through the hooks barrel: these component suites mock the barrel
// wholesale, and this hook must stay real for its analytics effects to be exercised there.
import { useTrialGateTracking } from '../../../hooks/useTrialGateTracking'
import { TrialGateSurface } from '../../../hooks/useTrialGate/trialGates'
import { theme } from '../../../modules/App/Providers/Theme/theme'
import { appointmentTypeToDisplayText } from 'util/'
import { deliveryModelToAppointmentTypes } from 'util/deliveryModelToAppointmentType'
import { useMemo } from 'react'

type AppointmentTypeSelectionProps = {
  onAppointmentTypeSelected: (appointmentTypeSelection: AppointmentType) => void
}

const DescriptionText = styled('div')`
  align-content: start;
  color: ${theme.palette.grey[500]};
  display: grid;
  font-size: 0.85rem;
`
const AppointmentTypeButton = styled(Button)`
  margin: 20px 20px 20px 0px;
`
// Off-screen rather than display:none or visibility:hidden, both of which remove the node from the
// accessibility tree - which would defeat the point.
const VisuallyHiddenReason = styled('span')`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

// Rendered after the always-available Hearing Aid Evaluation, in this order.
const FOLLOW_UP_APPOINTMENT_TYPES = [AppointmentType.FOLLOW_UP, AppointmentType.AFTERCARE_FOLLOW_UP]

const AppointmentTypeSelection: React.FC<AppointmentTypeSelectionProps> = ({ onAppointmentTypeSelected }) => {
  const makeOnAppointmentTypeClicked = (appointmentType: AppointmentType) => () => onAppointmentTypeSelected(appointmentType)
  const { t } = useTranslation()
  const searchParams = useSearchParameters()
  const { track: trackEvent } = useEventTracking()

  const journey = searchParams.journey as Journey | undefined
  const shouldRestrictToHAE = useShouldRestrictToHAE(journey)
  const deliveryModel = journey && journeyToJourneyDetails[journey]?.deliveryModel
  // The single source for what this screen shows: the buttons below render straight from it, so the
  // analytics denominator cannot drift from what the provider was actually offered.
  const offeredAppointmentTypes = useMemo(() => {
    if (shouldRestrictToHAE) return [AppointmentType.HEARING_AID_EVALUATION]
    const allowedAppointmentTypes = deliveryModelToAppointmentTypes(deliveryModel)
    return [
      AppointmentType.HEARING_AID_EVALUATION,
      ...FOLLOW_UP_APPOINTMENT_TYPES.filter((appointmentType) => allowedAppointmentTypes.includes(appointmentType)),
    ]
  }, [deliveryModel, shouldRestrictToHAE])

  const {
    activeTrialGateTypes,
    blockedAppointmentTypes,
    isTrialDataUnknown,
    pendingTrialGateTypes,
    reasons,
    trialEndDate,
    trialStatusSource,
  } = useBlockedAppointmentType()

  useTrialGateTracking({
    activeTrialGateTypes,
    blockedAppointmentTypes,
    isTrialDataUnknown,
    offeredAppointmentTypes,
    surface: TrialGateSurface.APPOINTMENT_TYPE_SELECTION,
    trackEvent,
    trialEndDate,
    trialStatusSource,
  })

  const renderAppointmentTypeButton = (appointmentType: AppointmentType) => {
    const label = appointmentTypeToDisplayText[appointmentType]

    // Held back while this direction might still turn out to be blocked. Leaving it live during that
    // window lets a quick click through to the form with the blocked type already selected, and
    // New/index.tsx builds its own form defaults from that selection, so it never reaches the
    // substitution in useScheduleFormDefaultValues. A direction whose flag is off is never pending,
    // so each still rolls out independently of the other.
    //
    // Defence in depth, and unreachable today: New is the only thing that renders this screen and it
    // folds the same isLoading into its own, so nothing here mounts while a direction is pending.
    // Kept because the two guard different things - New's gate protects the form default, which is
    // chosen once and never revisited, while this protects the click. A surface that rendered this
    // screen without New's gate would otherwise lose the protection silently.
    if (pendingTrialGateTypes.includes(appointmentType)) {
      return (
        <AppointmentTypeButton key={appointmentType} variant="outlined" disabled>
          {label}
        </AppointmentTypeButton>
      )
    }

    if (!blockedAppointmentTypes.includes(appointmentType)) {
      return (
        <AppointmentTypeButton key={appointmentType} variant="outlined" onClick={makeOnAppointmentTypeClicked(appointmentType)}>
          {label}
        </AppointmentTypeButton>
      )
    }

    // Disabled MUI buttons swallow hover events, so the tooltip has to attach to a wrapping span.
    // The tooltip alone reaches only pointer users - a disabled button is not focusable, so keyboard
    // and screen-reader users would get a dead control with no reason. The visually-hidden node
    // carries the same text into the accessibility tree, and aria-describedby ties it to the button.
    const reason = reasons[appointmentType] ?? ''
    const reasonId = `${appointmentType}-disabled-reason`

    return (
      <Tooltip key={appointmentType} title={reason}>
        <span>
          <AppointmentTypeButton variant="outlined" disabled aria-describedby={reasonId}>
            {label}
          </AppointmentTypeButton>
          <VisuallyHiddenReason id={reasonId}>{reason}</VisuallyHiddenReason>
        </span>
      </Tooltip>
    )
  }

  return (
    <Grid container spacing={1} style={{ justifyContent: 'center', maxWidth: '800px', margin: '0 auto' }}>
      <Grid item xs={9} style={{ paddingTop: '80px' }}>
        <Typography variant="h1">{t('common.label.selectedAppointmentType')}</Typography>
      </Grid>
      <Grid item xs={9} style={{ paddingTop: '20px' }}>
        <DescriptionText>
          This customer has had a hearing aid evaluation in the past.
          <br />
          Select the type of appointment you would like to schedule.
        </DescriptionText>
      </Grid>
      <Grid item xs={9}>
        {offeredAppointmentTypes.map(renderAppointmentTypeButton)}
      </Grid>
    </Grid>
  )
}

export { AppointmentTypeSelection }
